import getConfig from 'next/config';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { fetchUserGasRate } from '../actions/actionsSet/common';
import {
  fetchUserLogs,
  fetchUserConfig,
  fetchUserAntiPhishingCode,
} from '../actions/actionsSet/account';
import { fetchUserDevicesList } from '../actions/actionsSet/userDevice';
import { fetchUserDividends } from '../actions/actionsSet/asset';
import {
  fetchUserTradeFee,
  fetchAllUserAssets,
  fetchUserAssets,
  getUserBtcValuation,
  fetchFavoritesData,
} from '../actions/actionsSet/assetService';
import { fetchUserTradesData, fetchTradeOrders } from '../actions/actionsSet/order';
import { fetchListenKey, pingStreamer } from '../actions/actionsSet/userStream';

const {
  publicRuntimeConfig: { pingUserStreamerInterval },
} = getConfig();

const mapStateToProps = ({
  isLoggedIn, locale, listenKey, marginStatus,
}) => ({
  isLoggedIn, locale, listenKey, marginStatus,
});
const mapDispatchToProps = {
  fetchFavoritesData,
  fetchUserTradesData,
  fetchTradeOrders,
  fetchUserDevicesList,
  fetchUserAntiPhishingCode,
  fetchUserAssets,
  fetchUserDividends,
  fetchUserLogs,
  fetchAllUserAssets,
  getUserBtcValuation,
  fetchUserTradeFee,
  pingStreamer,
  fetchListenKey,
  fetchUserGasRate,
  fetchUserConfig,
};

export default
@connect(
  mapStateToProps,
  mapDispatchToProps
)
class UserDataProvider extends Component {
  static propTypes = {
    isLoggedIn: PropTypes.bool,
    fetchFavoritesData: PropTypes.func.isRequired,
    fetchUserTradesData: PropTypes.func.isRequired,
    fetchTradeOrders: PropTypes.func.isRequired,
    fetchUserAssets: PropTypes.func.isRequired,
    fetchUserDividends: PropTypes.func.isRequired,
    fetchUserLogs: PropTypes.func.isRequired,
    fetchUserDevicesList: PropTypes.func.isRequired,
    fetchUserAntiPhishingCode: PropTypes.func.isRequired,
    fetchAllUserAssets: PropTypes.func.isRequired,
    getUserBtcValuation: PropTypes.func.isRequired,
    fetchUserTradeFee: PropTypes.func.isRequired,
    shouldRefresh: PropTypes.bool,
    userTrades: PropTypes.array,
    tradeOrders: PropTypes.array,
    both: PropTypes.array || null,
    children: PropTypes.node,
    pro: PropTypes.bool,
    listenKey: PropTypes.string,
    wssUri: PropTypes.string,
    pingStreamer: PropTypes.func,
    locale: PropTypes.string,
    fetchListenKey: PropTypes.func,
    fetchUserGasRate: PropTypes.func.isRequired,
    userFunds: PropTypes.bool,
    userCenter: PropTypes.bool,
    coin: PropTypes.string,
    fetchUserConfig: PropTypes.func,
    enableGasRate: PropTypes.bool,
    marginStatus: PropTypes.bool,
  };

  static defaultProps = {
    children: null,
    both: null,
    userTrades: [],
    tradeOrders: [],
    shouldRefresh: false,
    pro: false,
    userFunds: false,
    userCenter: false,
    listenKey: '',
    wssUri: '',
    pingStreamer: () => {},
    locale: 'en',
    fetchListenKey: () => {},
    fetchUserConfig: () => {},
    coin: 'btc',
    enableGasRate: true,
  };

  // client-side (component imported with `next/dynamic`)
  componentDidMount() {
    this.props.enableGasRate && this.props.fetchUserGasRate();

    this._fetch(this.props);
    if (this.props.userFunds || this.props.userCenter) {
      return;
    }
    const self = this;

    if (self.props.listenKey) {
      setInterval(() => {
        self.props.pingStreamer(self.props.listenKey);
      }, pingUserStreamerInterval);
    }
  }

  shouldComponentUpdate({
    listenKey, both, coin, isLoggedIn, marginStatus,
  }) {
    const {
      listenKey: prevListenKey,
      both: prevBoth,
      coin: preCoin,
      isLoggedIn: preIsLoggedIn,
      marginStatus: preMarginStatus,
    } = this.props;

    if (
      listenKey !== prevListenKey ||
      both !== prevBoth ||
      coin !== preCoin ||
      isLoggedIn !== preIsLoggedIn ||
      marginStatus !== preMarginStatus
    ) {
      return true;
    }
    return false;
  }

  componentDidUpdate(prevProps) {
    const {
      coin, both, isLoggedIn, marginStatus,
    } = this.props;
    if (
      prevProps.coin !== coin ||
      prevProps.both !== both ||
      prevProps.isLoggedIn !== isLoggedIn
    ) {
      this._fetch(this.props);
    }

    if (prevProps.marginStatus !== marginStatus) { this._fetchUserTrades(this.props); }
  }

  _fetch(props) {
    const {
      locale, isLoggedIn, userFunds, userCenter,
    } = props;
    if (!isLoggedIn) return;
    if (userFunds) {
      props.fetchAllUserAssets(locale);
    }

    if (userCenter) {
      props.fetchUserDevicesList(locale);
      props.fetchUserAntiPhishingCode(locale);
      props.fetchUserDividends(
        {
          page: 1,
          rows: 5,
        },
        locale
      );
      props.fetchUserLogs(
        {
          page: 1,
          rows: 5,
        },
        locale
      );
      return;
    }

    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 1);
    props.fetchUserConfig();
    props.fetchFavoritesData(locale);
    props.fetchTradeOrders( // todo move to TradingDataProvider?
      {
        startTime: start.getTime(),
        endTime: end.getTime(),
        page: 1,
        rows: 200,
      },
      locale,
      false,
      false
    );

    const { both, pro } = props;
    if (both) {
      const symbol = `${both[0]}${both[1]}`;
      if (!pro) {
        // pro page update in component
        props.fetchUserTradesData(
          {
            symbol,
            startTime: start.getTime(),
            endTime: end.getTime(),
            page: 1,
            rows: 200,
          },
          locale
        );
      }
      props.fetchUserAssets(both, locale);
      props.fetchUserTradeFee(symbol, locale);
    }

    if (pro) {
      props.fetchAllUserAssets(locale);
      props.getUserBtcValuation(locale);
    }
    props.fetchListenKey();
  }

  _fetchUserTrades(props) {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 1);
    const { both, locale, pro } = props;
    if (both) {
      const symbol = `${both[0]}${both[1]}`;
      if (!pro) {
        // pro page update in component
        props.fetchUserTradesData(
          {
            symbol,
            startTime: start.getTime(),
            endTime: end.getTime(),
            page: 1,
            rows: 40,
          },
          locale,
          false
        );
      }
    }
  }

  render() {
    return null;
  }
}
